import React, {createContext, useState, useEffect, useMemo, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import queryString from 'query-string';
import {parseQueryParams, haversineDistance} from '../helpers/base';
import useIsMobile from '../hooks/useIsMobile';
import usePrevious from '../hooks/usePrevious';


export const AppContext = createContext();

export const AppProvider = ({children, endpoint}) => {

    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile(992);
    const currentFilters = parseQueryParams(location);
    const locale = document.documentElement.lang?.substring(0, 2) ?? 'de';

    const [isInitializing, setIsInitializing] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState([]);
    const [results, setResults] = useState([]);
    const [isFilterOverlayVisible, setIsFilterOverlayVisible] = useState(false);
    const [isSortOverlayVisible, setIsSortOverlayVisible] = useState(false);
    const [isDistanceOverlayVisible, setIsDistanceOverlayVisible] = useState(false);
    const [isBookingBoxOverlayVisible, setIsBookingBoxOverlayVisible] = useState(false);
    const [hasResultsWithPrices, setHasResultsWithPrices] = useState(false);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [currentMapPoi, setCurrentMapPoi] = useState(null);
    const [filteredResults, setFilteredResults] = useState([]);
    const [filteredResultsWithoutPriceFilter, setFilteredResultsWithoutPriceFilter] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState(currentFilters);
    const [subFilters, setSubFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(currentFilters.page ? Number(currentFilters.page) : 1);
    const [endpointParams, setEndpointParams] = useState(currentFilters);
    const [allFilters, setAllFilters] = useState([]);
    const [poolEnquiryData, setPoolEnquiryData] = useState([]);
    const [resultsCache, setResultsCache] = useState({});
    const itemsPerPage = 12;
    const previousFilters = usePrevious(selectedFilters);

    const applyFilters = useCallback((selectedFilters, subFilters, results) => {

        const filteredResults = [...results];

        const propertyMap = {
            accoType: {property: 'accommodationTypeId', type: 'int'},
            awards: {property: 'ratingAwardId', type: 'int'},
            topics: {property: 'features', type: 'array'},
            specializations: {property: 'specializations', type: 'array'},
            specialOffers: {property: 'specialOffers', type: 'array'},
            region: {property: 'regions', type: 'array'},
            state: {property: 'stateId', type: 'int'},
            accoFeatures: {property: 'features', type: 'array'},
            animals: {property: 'animals', type: 'array'},
            locations: {property: 'locations', type: 'array'},
            mealPlans: {property: 'mealPlans', type: 'array'},
            roomTypes: {property: 'roomTypes', type: 'array'},
            experienceWorlds: {property: 'experienceWorlds', type: 'array'},
            accessible: {property: 'accessibleFacilities', type: 'int'},
            petsAllowed: {property: 'petsAllowed', type: 'int'},
        };

        const preprocessedFilters = [];

        Object.entries(selectedFilters).forEach(([filterType, filterValue]) => {
            const mapEntry = propertyMap[filterType];
            if (
                !mapEntry ||
                (Array.isArray(filterValue) && filterValue.length === 0) ||
                (!Array.isArray(filterValue) && (filterValue === null || filterValue === undefined))
            ) {
                return;
            }

            if (Array.isArray(filterValue)) {
                // Group filter values by which subFilter section they belong to
                const groupedBySubFilter = {};

                Object.entries(subFilters).forEach(([groupKey, { name, options }]) => {
                    if (name === filterType) {
                        const optionIds = options.map(opt => opt.id);
                        const matched = filterValue.filter(val => optionIds.includes(val));
                        if (matched.length > 0) {
                            if (!groupedBySubFilter[groupKey]) {
                                groupedBySubFilter[groupKey] = [];
                            }
                            groupedBySubFilter[groupKey].push(...matched);
                        }
                    }
                });

                // If not found in subFilters, fallback to one group
                const groupedValues = Object.keys(groupedBySubFilter).length
                    ? Object.values(groupedBySubFilter)
                    : [filterValue];

                groupedValues.forEach(values => {
                    preprocessedFilters.push({
                        filterType,
                        filterValue: values,
                        property: mapEntry.property,
                        type: mapEntry.type,
                    });
                });
            } else {
                preprocessedFilters.push({
                    filterType,
                    filterValue,
                    property: mapEntry.property,
                    type: mapEntry.type,
                });
            }
        });

        let filtered = filteredResults.filter(result => {
            for (const {filterValue, property, type} of preprocessedFilters) {
                if (type === 'int') {
                    if (Array.isArray(filterValue)) {
                        if (!filterValue.includes(result[property])) {
                            return false;
                        }
                    } else if (filterValue !== result[property]) {
                        return false;
                    }
                } else if (type === 'array') {
                    if (Array.isArray(filterValue)) {
                        if (!filterValue.some(id => result[property]?.includes(id))) {
                            return false;
                        }
                    } else {
                        if (!result[property]?.includes(filterValue)) {
                            return false;
                        }
                    }
                } else {
                    console.warn(`Unsupported property type: ${type}`);
                    return false;
                }
            }
            return true;
        });

        if (selectedFilters.lat && selectedFilters.lng) {
            const {lat, lng, distance} = selectedFilters;
            let selectedDistance = distance ? Number(distance) : 20;
            filtered = filtered.filter(result => {
                if (result.latitude && result.longitude) {
                    const distance = haversineDistance(lat, lng, result.latitude, result.longitude);
                    if (distance <= selectedDistance) {
                        result.distance = distance;
                        return true;
                    }
                    return false;
                }
                return false;
            });
        }

        setFilteredResultsWithoutPriceFilter([...filtered]);

        if (selectedFilters.price) {
            const [minPrice, maxPrice] = selectedFilters.price.split('-').map(Number);
            filtered = filtered.filter(result => {
                return result.price >= minPrice && result.price <= maxPrice;
            });
        }

        if (selectedFilters.sort) {
            filtered = applySorting(selectedFilters.sort, filtered);
        }

        setFilteredResults(filtered);
    }, []);

    const applySorting = (sortBy, filteredResults) => {

        const sortedResults = [...filteredResults];

        const compareByAward = (a, b, orderKey, isAscending = false) => {

            if (a.ratingScoreId === null) return 1;
            if (b.ratingScoreId === null) return -1;

            let aIndex = orderKey.indexOf(a.ratingScoreId);
            let bIndex = orderKey.indexOf(b.ratingScoreId);

            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;

            if (a.ratingScoreId !== null && b.ratingScoreId !== null) {
                return isAscending ? a.ratingScoreId - b.ratingScoreId : b.ratingScoreId - a.ratingScoreId;
            }

            return 0;
        };

        switch (sortBy) {
            case 'price-asc':
                sortedResults.sort((a, b) => a.price - b.price);
                break;
            case 'price-desc':
                sortedResults.sort((a, b) => b.price - a.price);
                break;
            case 'distance':
                sortedResults.sort((a, b) => a.distance - b.distance);
                break;
            case 'award-asc':
                // aufsteigend: almhütte (9), premiumhütte (8), codex(10)
                sortedResults.sort((a, b) => compareByAward(a, b, [9, 8, 10], true));
                break;
            case 'award-desc':
                // absteigend: premiumhütte (8), almhütte (9), codex(10)
                sortedResults.sort((a, b) => compareByAward(a, b, [8, 9, 10]));
                break;
            case 'rating-desc':
                sortedResults.sort((a, b) => b.trustYouScore - a.trustYouScore);
                break;
            default:
                break;
        }

        return sortedResults;
    };

    const getSubFilters = (filters, accommodationType = null) => {
        let newSubFilters = {};
        if(accommodationType) {
            const accommodation = filters.accommodationType.options.find(
                (item) => item.id === accommodationType
            );
            if (accommodation && accommodation.filters) {
                accommodation.filters.forEach((filter, index) => {
                    newSubFilters[index] = {
                        ...filter,
                        options: [...new Map(filter.options.map(option => [option.id, option])).values()]
                    };
                });
            }
        }
        return newSubFilters;
    };

    const handlePageChange = (page) => {
        setPage(page);
    };

    const setPage = (page) => {
        setCurrentPage(page);
        const queryParams = queryString.stringify({...selectedFilters, page}, {arrayFormat: 'bracket'});
        navigate(`?${queryParams}`, {replace: true});
        window.requestAnimationFrame(() => window.scrollTo({top: 0, behavior: 'instant'}));
    };

    const handleAccommodationTypeChange = (accommodationType) => {

        const newFilters = {
            ...selectedFilters
        };

        if (accommodationType) {
            newFilters.accoType = accommodationType;
        } else {
            delete newFilters.accoType;
        }

        const newSubFilters = getSubFilters(filters, accommodationType);
        let currentFilters = filters.current && !Array.isArray(filters.current) ? filters.current : {};

        for (const [filterType, optionIds] of Object.entries(newFilters)) {

            if (!Array.isArray(optionIds)) {
                continue;
            }

            if (['region'].includes(filterType)) {
                continue;
            }

            const options = newSubFilters[filterType]?.options || [];
            const currentOptions = currentFilters[filterType] || [];

            // Merge current filters into options, in case they are missing in the normal filters
            currentOptions.forEach(option => {
                if (!options.some(o => o.id === option.id)) {
                    options.push(option);
                }
            });

            // Filter valid values based on option IDs
            const validOptionIds = new Set(options.map(option => option.id));
            newFilters[filterType] = optionIds.filter(optionId => validOptionIds.has(optionId));
        }

        setSelectedFilters(newFilters);
        setSubFilters(newSubFilters);
    };

    const handleFilterChange = (filterType, filterValue, isSelected) => {

        setSelectedFilters(prev => {
            const filters = {...prev};
            if (Array.isArray(filters[filterType])) {
                if (isSelected) {
                    filters[filterType].push(filterValue);
                } else {
                    filters[filterType] = filters[filterType].filter(value => value !== filterValue);
                    if (filters[filterType].length === 0) {
                        delete filters[filterType];
                    }
                }
            } else {
                if (isSelected) {
                    filters[filterType] = [filterValue];
                } else {
                    delete filters[filterType];
                }
            }

            if (['q', 'region'].includes(filterType) && !isSelected) {
                delete filters.lat;
                delete filters.lng;
                delete filters.state;
                delete filters.q;
                delete filters.distance;
                if (filters.sort === 'distance') {
                    delete filters.sort;
                }
            }

            return filters;
        });

        if (hasResultsWithPrices && ['mealPlans', 'roomTypes'].includes(filterType)) {

            if (isSelected) {
                if (endpointParams[filterType]) {
                    setEndpointParams({
                        ...endpointParams,
                        [filterType]: [...endpointParams[filterType], filterValue]
                    });
                } else {
                    setEndpointParams({
                        ...endpointParams,
                        [filterType]: [filterValue]
                    });
                }
            } else {
                if (endpointParams[filterType]) {
                    setEndpointParams({
                        ...endpointParams,
                        [filterType]: endpointParams[filterType].filter((item) => item !== filterValue)
                    });
                }
            }
        }
    };

    const handleSimpleFilterChange = (filterType, isSelected) => {
        setSelectedFilters(prev => {
            const filters = {...prev};
            if (isSelected) {
                filters[filterType] = 1;
            } else {
                delete filters[filterType];
            }
            return filters;
        });
    };

    const handlePriceRangeChange = (min, max) => {
        setSelectedFilters(prev => ({...prev, price: min + '-' + max}));
    };

    const handleSortChange = (value) => {
        setSelectedFilters(prev => ({
            ...prev,
            sort: value
        }));
    };

    const handleSearchQueryChange = (option) => {
        const {value, additionalInputs} = option;
        const {state, region, lat, lng, accommodation} = additionalInputs;

        if (accommodation) {
            // redirect directly to the detail page
            const result = results.find(result => result.accommodationId === accommodation);
            if (result && result.detailPageUrl) {
                window.location = result.detailPageUrl;
                return;
            }
        }

        const filters = {
            ...selectedFilters,
            q: value,
        };

        if (lat && lng) {
            filters.lat = lat;
            filters.lng = lng;
            filters.sort = 'distance';
        } else {
            delete filters.lat;
            delete filters.lng;
            if (filters.sort === 'distance') {
                delete filters.sort;
            }
        }

        if (state) {
            filters.state = state;
        } else {
            delete filters.state;
        }

        if (region) {
            if (!Array.isArray(filters.region)) {
                filters.region = [];
            }
            if (!filters.region.includes(region)) {
                filters.region.push(region);
            }

            const regionExists = allFilters.some(filter => filter.id === region.id);
            if (!regionExists) {
                allFilters.push({id: region, label: value});
                setAllFilters(allFilters);
            }
        }

        if (Array.isArray(filters.region) && filters.region.length === 0) {
            delete filters.region;
        }

        setSelectedFilters(filters);
    };

    const handleClearFilters = () => {
        setSelectedFilters({});
    };

    const handleSubmitSearch = (selectedDates, rooms) => {

        const dates = {};

        if (selectedDates.length === 2) {
            dates.checkin = selectedDates[0].toLocaleDateString('en-CA');
            dates.checkout = selectedDates[1].toLocaleDateString('en-CA');
        } else {
            dates.checkin = undefined;
            dates.checkout = undefined;
        }

        const occupancy = {};

        rooms.forEach((group, index) => {
            occupancy[`adults-${index}`] = group.adults;
            occupancy[`childs-${index}`] = group.children;

            group.childAges.forEach((age, childIndex) => {
                occupancy[`childAge${index}-${childIndex + 1}`] = age;
            });
        });

        setSelectedFilters(prev => {

            const filters = Object.keys(prev).reduce((acc, key) => {
                if (!key.startsWith('adults-') && !key.startsWith('childs-') && !key.startsWith('childAge')) {
                    acc[key] = prev[key];
                }
                return acc;
            }, {});

            return {
                ...filters,
                ...occupancy,
                ...dates
            };
        });

        setEndpointParams(prev => ({
            ...prev,
            ...occupancy,
            ...dates
        }));

        setIsBookingBoxOverlayVisible(false);
    };

    const handleToggleMap = () => {
        setSelectedFilters(prev => ({
            ...prev,
            showMap: Number(!showMap)
        }));
        setShowMap(prev => !prev);
        setCurrentMapPoi(null);
    };

    const handleDistanceChange = (distance) => {
        setSelectedFilters(prev => ({
            ...prev,
            distance: Number(distance)
        }));
    };

    useEffect(() => {
        const seedLifetime = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        const setNewSeed = () => {
            const newSeed = Math.floor((Math.random() + Math.random() * Date.now() % 1000) * 5000) + 1;
            try {
                window.localStorage.setItem('sortingSeed', newSeed.toString());
                window.localStorage.setItem('sortingSeedDate', Date.now().toString());
            } catch (e) {
                console.error('Failed to access localStorage:', e);
            }
        };

        const retrieveSeedData = () => {
            try {
                const seed = window.localStorage.getItem('sortingSeed');
                const date = window.localStorage.getItem('sortingSeedDate');
                return {seed, date};
            } catch (e) {
                console.error('Failed to access localStorage:', e);
                return {seed: null, date: null};
            }
        };

        const {seed, date} = retrieveSeedData();
        const now = Date.now();

        const seedNumber = parseInt(seed, 10);
        const dateNumber = parseInt(date, 10);

        // If the seed is missing, date is missing, or the seed has expired, set a new one
        if (
            isNaN(seedNumber) ||
            isNaN(dateNumber) ||
            (now - dateNumber > seedLifetime)
        ) {
            setNewSeed();
        }
    }, []);

    useEffect(() => {
        try {
            let sessionData = window.sessionStorage.getItem('accommodations');
            let data = sessionData !== null ? JSON.parse(sessionData) : [];
            setPoolEnquiryData(data);
        } catch (err) {
            // console.warn(`pool-enquiry: sessionStorage accommodations getItem failed!`);
        }
    }, []);

    useEffect(() => {
        const collectFilterOptions = (data, existingFilters) => {

            const allFilters = [...existingFilters];
            const uniqueIds = new Set(allFilters.map(filter => filter.id));

            const addOptions = (options) => {
                options.forEach(option => {
                    if (!uniqueIds.has(option.id)) {
                        uniqueIds.add(option.id);
                        allFilters.push({
                            id: option.id,
                            label: option.label
                        });
                    }
                });
            };

            if (data.accommodationType && data.accommodationType.options) {
                data.accommodationType.options.forEach(accommodationType => {
                    if (!uniqueIds.has(accommodationType.id)) {
                        uniqueIds.add(accommodationType.id);
                        allFilters.push({
                            id: accommodationType.id,
                            label: accommodationType.label
                        });
                    }
                    accommodationType.filters.forEach(filter => {
                        addOptions(filter.options);
                    });
                });
            }

            if (data.global) {
                Object.values(data.global).forEach(globalFilter => {
                    addOptions(globalFilter.options);
                });
            }

            if (data.current && !Array.isArray(data.current)) {
                Object.values(data.current).forEach(options => addOptions(options));
            }

            return allFilters;
        };

        setAllFilters(prevFilters => collectFilterOptions(filters, prevFilters));
    }, [filters]);

    useEffect(() => {

        const existingParams = queryString.parseUrl(endpoint, {
            arrayFormat: 'bracket'
        }).query;

        const queryParams = queryString.stringify({...existingParams, ...endpointParams}, {arrayFormat: 'bracket'});
        const finalEndpoint = `${queryString.parseUrl(endpoint).url}?${queryParams}`;

        const fetchData = () => {
            const cacheKey = queryParams;

            if (resultsCache[cacheKey]) {
                return Promise.resolve(resultsCache[cacheKey]);
            }

            setIsLoading(true);
            return fetch(finalEndpoint, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-Sorting-Seed': localStorage.getItem('sortingSeed')
                }
            })
                .then(response => response.json())
                .then(data => {
                    setResultsCache(prev => ({
                        ...prev,
                        [cacheKey]: data
                    }));
                    return data;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        };

        const handleResponse = (data) => {
            const {filters, results, userLoggedIn, filterIdsRequestUrl} = data;
            setFilters(filters);
            setResults(results);
            setIsUserLoggedIn(userLoggedIn);
            setSubFilters(getSubFilters(filters, selectedFilters.accoType));
            window._config.filterIdsRequestUrl = filterIdsRequestUrl;
            window._config.filterIdsRequestUrlSingle = filterIdsRequestUrl;
            setTimeout(() => {
                setIsInitializing(false);
                setIsLoading(false);
            }, 0);
        };

        fetchData()
            .then(handleResponse)
            .catch(error => {
                console.error('Error fetching data:', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, endpointParams]);

    useEffect(() => {

        applyFilters(selectedFilters, subFilters, results);

        if (!isInitializing && currentPage > 1) {
            selectedFilters.page = 1;
            setPage(1);
        }

        const args = {...selectedFilters};

        if (selectedFilters.checkin && selectedFilters.checkout && selectedFilters['adults-0']) {
            setHasResultsWithPrices(true);
        } else {
            setHasResultsWithPrices(false);
        }

        if (selectedFilters.showMap) {
            setShowMap(true);
            document.body.classList.add('is-fullscreen');
        } else {
            document.body.classList.remove('is-fullscreen');
        }

        const queryParams = queryString.stringify(args, {arrayFormat: 'bracket'});
        navigate(`?${queryParams}`, {replace: true});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters, results, applyFilters, navigate]);

    // push filter changes to dataLayer
    useEffect(() => {

        if (!selectedFilters || Object.keys(selectedFilters).length === 0) {
            return;
        }

        const excludedKeys = ['page'];
        const filteredFilters = {...selectedFilters};
        Object.keys(filteredFilters).forEach(key => {
            if (excludedKeys.includes(key) || filteredFilters[key] === undefined) {
                delete filteredFilters[key];
            }
        });

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'accommodationFiltered',
            'accommodationFilterObject': filteredFilters
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]);

    useEffect(() => {
        if(isFilterOverlayVisible || isBookingBoxOverlayVisible || isSortOverlayVisible) {
            document.body.style.position = 'fixed';
        } else {
            document.body.style.position = '';
        }
    }, [isFilterOverlayVisible, isBookingBoxOverlayVisible, isSortOverlayVisible]);


    return (
        <AppContext.Provider value={useMemo(() => ({
            isInitializing,
            isLoading,
            locale,
            filters,
            subFilters,
            results,
            filteredResults,
            filteredResultsWithoutPriceFilter,
            selectedFilters,
            setSelectedFilters,
            allFilters,
            currentPage,
            itemsPerPage,
            isSortOverlayVisible,
            setIsSortOverlayVisible,
            isFilterOverlayVisible,
            setIsFilterOverlayVisible,
            isBookingBoxOverlayVisible,
            setIsBookingBoxOverlayVisible,
            isDistanceOverlayVisible,
            setIsDistanceOverlayVisible,
            hasResultsWithPrices,
            showMap,
            setShowMap,
            currentMapPoi,
            setCurrentMapPoi,
            isMobile,
            isUserLoggedIn,
            poolEnquiryData,
            handlePageChange,
            handleAccommodationTypeChange,
            handleFilterChange,
            handleSimpleFilterChange,
            handlePriceRangeChange,
            handleSortChange,
            handleToggleMap,
            handleDistanceChange,
            handleSearchQueryChange,
            handleClearFilters,
            handleSubmitSearch
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }), [
            isInitializing,
            isLoading,
            filteredResults,
            subFilters,
            showMap,
            currentMapPoi,
            isMobile,
            currentPage,
            isSortOverlayVisible,
            isFilterOverlayVisible,
            isBookingBoxOverlayVisible,
            isDistanceOverlayVisible
        ])}>
            {children}
        </AppContext.Provider>
    );
};